import {Component, effect, inject, signal} from '@angular/core';
import {TaskTable} from './task-table/task-table.component';
import {ItemCounterComponent} from '../../common/item-counter/item-counter.component';
import {MatFormField, MatFormFieldModule, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {BadgeUserData, PersonPickerComponent} from '../../common/person-picker/person-picker.component';
import {DeadlineFilterChange, DeadlineFilterComponent} from '../../common/deadline-filter/deadline-filter.component';
import {TaskService} from '../../services/task.service';
import {Task, TaskCountsResponse, TaskFilterParams} from '../../../models/task';
import {MatDialog} from '@angular/material/dialog';
import {CreateTaskComponent} from './create-task/create-task.component';
import dayjs from 'dayjs';
import {User} from '../../../models/user';
import {UserService} from '../../services/user.service';
import {ActivatedRoute, Router} from '@angular/router';
import {MentionsDirective} from '../../common/mentions.directive';
import {StatusGroup} from '../../common/status-parser.pipe';
import {Location} from '@angular/common';
import {SnackbarService} from '../../services/snackbar.service';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatSelectModule} from '@angular/material/select';
import {ButtonWLoaderComponent} from '../../common/button-w-loader/button-w-loader.component';
import {SelectFilterComponent} from '../../common/select-filter/select-filter.component';
import {MatTooltip} from '@angular/material/tooltip';
import {PageEvent} from '@angular/material/paginator';
import {Sort} from '@angular/material/sort';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
@Component({
  selector: 'app-tasks',
  imports: [
    TaskTable,
    ItemCounterComponent,
    MatFormField,
    MatInput,
    MatLabel,
    PersonPickerComponent,
    DeadlineFilterComponent,
    MentionsDirective,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonWLoaderComponent,
    SelectFilterComponent,
    MatTooltip
  ],
  templateUrl: './tasks.component.html',
  standalone: true,
  styleUrl: './tasks.component.scss'
})
export class TasksComponent {
  private readonly sortColumnMap: Record<string, NonNullable<TaskFilterParams['sortBy']>> = {
    _id: 'alias',
    _name: 'name',
    status: 'status',
    deadline: 'deadline',
  };

  currentSort: Pick<TaskFilterParams, 'sortBy' | 'sortOrder'> = {};

  statusMap = new Map<string, StatusGroup>([
    ['total', StatusGroup.total.setShowSeparator(true)],
    ['open', StatusGroup.open],
    ['inProgress', StatusGroup.inProgress],
    ['done', StatusGroup.done.setShowSeparator(true)],
    ['late', StatusGroup.late],
    ['ok', StatusGroup.ok],
  ]);

  toBeDeleted = '';
  statusOptions = [
    { value: 'open', label: "Aberto"},
    { value: 'inProgress', label: "Em progresso"},
    { value: 'done', label: "Concluido"}];
  statusFilter = new FormControl(['open', 'inProgress']);

  usersService = inject(UserService);
  usersToShowBadges?: User[] = [];
  selectedPeople: string[] = [];

  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);

  loading = true;
  tid?: string;
  pageSize = signal(10);

  private searchSubject = new Subject<string>();
  private searchValue = '';
  private deadlineFilter = '';

  constructor(
    public taskService: TaskService,
    private snackBar: SnackbarService
  ) {

    this.activatedRoute.paramMap.subscribe((param) => {
      this.tid = param.get('taskId') ?? undefined;
    });
    effect(() => {
      // Read signals to track them
      const filters = this.taskService.taskFilters();
      const tasks = this.taskService.taskList();
      const counts = this.taskService.taskCounts();
      this.loading = false;

      // Get users from filters or extract from tasks
      if (filters?.users?.length) {
        this.usersToShowBadges = filters.users.map(u => ({ id: u.id, name: u.name, badgeColor: u.badgeColor } as User));
      } else {
        this.usersToShowBadges = this.extractUsersFromTasks(tasks);
      }

      this.setStatusMap(counts);
      if (this.usersService.users().length && this.tid) {
        const tid = this.tid;
        this.tid = undefined;
        const task = tasks.find((t) => t.id === tid);
        if (task) {
          this.openDialog(tid);
        } else {
          this.taskService.getTaskById(tid).then(taskData => {
            this.openDialogWithData(taskData);
          });
        }
      }
    });

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchValue => {
      this.searchValue = searchValue;
      this.fetchTasks();
    });

    this.statusFilter.valueChanges.subscribe(() => {
      this.fetchTasks();
    });

    this.fetchTasks();
  }

  fetchTasks() {
    this.taskService.getTasksPaginated({
      search: this.searchValue || undefined,
      users: this.selectedPeople.length > 0 ? this.selectedPeople : undefined,
      status: this.statusFilter.value?.length ? this.statusFilter.value : undefined,
      deadline: this.deadlineFilter || undefined,
      offset: 0,
      limit: this.pageSize(),
      ...this.currentSort,
    });
  }

  setStatusMap(counts: TaskCountsResponse | null) {
    this.statusMap.get('total')?.setCount(this.taskService.taskPagination()?.total ?? 0);
    this.statusMap.get('open')?.setCount(counts?.open ?? 0);
    this.statusMap.get('inProgress')?.setCount(counts?.inProgress ?? 0);
    this.statusMap.get('done')?.setCount(counts?.done ?? 0);
    this.statusMap.get('ok')?.setCount(counts?.onTime ?? 0);
    this.statusMap.get('late')?.setCount(counts?.overdue ?? 0);
  }

  private extractUsersFromTasks(tasks: Task[]): User[] {
    const userMap = new Map<string, User>();
    for (const task of tasks) {
      if (task.assigneeUser && !userMap.has(task.assigneeUser.id)) {
        userMap.set(task.assigneeUser.id, task.assigneeUser);
      }
    }
    return Array.from(userMap.values());
  }

  readonly dialog = inject(MatDialog);
  usersToFilter: User[] = []

  opened = false;
  location = inject(Location);
  openDialog(taskId?: string): void {
    const taskData = taskId ? this.taskService.taskList().find((task) => task.id === taskId) : undefined;
    this.openDialogWithData(taskData);
  }

  private openDialogWithData(taskData?: Task): void {
    if (this.opened) {
      return;
    }
    const dialogRef = this.dialog.open(CreateTaskComponent, {
      minWidth: '1120px',
      height: '700px',
      data: {
        taskData,
        users: this.usersService.users()
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      this.tid = undefined;
      this.opened = false;
      this.location.go('tarefas');
      if (result) {
        if (result.deleteId) {
          this.toBeDeleted = result.deleteId;
        } else if (result.newTask) {
          const snack = this.snackBar.openSnackBar('success',
            'Tarefa salva com sucesso!',
            'Clique para vê-la'
          );
          snack.onAction().subscribe(() => {
            this.router.navigate(['tarefas', result.task.id]);
          });
          this.loading = true;
        }
      }
    });
  }

  deadlineChange(event: DeadlineFilterChange) {
    if (event.today) {
      this.deadlineFilter = 'today';
    } else if (event.late) {
      this.deadlineFilter = 'overdue';
    } else if (event.until) {
      this.deadlineFilter = 'upcoming';
    } else {
      this.deadlineFilter = '';
    }
    this.fetchTasks();
  }

  deadlineDateChange(event?: Date) {
    if (event) {
      this.deadlineFilter = dayjs(event).format('YYYY-MM-DD');
    } else {
      this.deadlineFilter = '';
    }
    this.fetchTasks();
  }

  newTask() {
    this.openDialog();
  }

  openDetails(taskId: string) {
    this.openDialog(taskId);
  }

  search(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchSubject.next(filterValue);
  }

  personSelected(event: BadgeUserData[]) {
    this.selectedPeople = event.filter((user) => user.selected).map((user) => user.id);
    this.fetchTasks();
  }

  onPageChange(event: PageEvent) {
    this.pageSize.set(event.pageSize);
    this.taskService.getTasksPaginated({
      search: this.searchValue || undefined,
      users: this.selectedPeople.length > 0 ? this.selectedPeople : undefined,
      status: this.statusFilter.value?.length ? this.statusFilter.value : undefined,
      deadline: this.deadlineFilter || undefined,
      offset: event.pageIndex * event.pageSize,
      limit: event.pageSize,
      ...this.currentSort,
    });
  }

  onSortChange(event: Sort) {
    const apiColumn = this.sortColumnMap[event.active];
    if (!apiColumn || !event.direction) {
      this.currentSort = {};
    } else {
      this.currentSort = { sortBy: apiColumn, sortOrder: event.direction as 'asc' | 'desc' };
    }
    this.fetchTasks();
  }
}
